import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Heart, Search, SlidersHorizontal, Menu, X, User, LogOut, Package } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { useFilters } from "../context/FilterContext";
import { useAuth } from "../context/AuthContext";
import { useEffect, useState } from "react";
import SearchDropdown from "./SearchDropdown";

export default function Navbar() {
  const { itemCount, setCartOpen, wishlist } = useStore();
  const { setOpen: setFiltersOpen, isFiltering } = useFilters();
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = () => setAccountOpen(false);
    if (accountOpen) document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [accountOpen]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#0A0A0A]/55 backdrop-blur-2xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 flex items-center justify-between h-[68px] gap-4">
        {/* Left: Logo + Filters button */}
        <div className="flex items-center gap-6 md:gap-10">
          <Link
            to="/"
            data-testid="brand-logo"
            className="font-serif-display whitespace-nowrap"
            style={{ fontSize: 22, fontWeight: 600, color: "#F5D565", letterSpacing: "0.04em" }}
          >
            Her Box
          </Link>
          <button
            onClick={() => setFiltersOpen((o) => !o)}
            data-testid="header-filters-btn"
            className="hidden md:inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#D4D4D4] hover:text-[#D4AF37] transition-colors relative"
            aria-label="Toggle filters"
          >
            <SlidersHorizontal size={14} strokeWidth={1.5} />
            Filters
            {isFiltering && (
              <span className="absolute -top-1 -right-3 w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
            )}
          </button>
        </div>

        {/* Mobile menu button */}
        <button
          onClick={() => setMobileOpen(true)}
          className="md:hidden text-[#F5F5F5]"
          data-testid="mobile-menu-btn"
        >
          <Menu strokeWidth={1.25} size={22} />
        </button>

        {/* Right: Icons */}
        <div className="flex items-center gap-5 text-[#D4D4D4]">
          <button
            data-testid="search-btn"
            onClick={() => setSearchOpen(true)}
            className="hover:text-[#D4AF37] transition-colors inline-flex"
            aria-label="Search"
          >
            <Search strokeWidth={1.25} size={20} />
          </button>
          <Link
            to="/wishlist"
            data-testid="wishlist-btn"
            className="hover:text-[#D4AF37] transition-colors relative"
            aria-label="Wishlist"
          >
            <Heart strokeWidth={1.25} size={20} />
            {wishlist.length > 0 && (
              <span className="absolute -top-1 -right-2 text-[10px] text-[#D4AF37]">
                {wishlist.length}
              </span>
            )}
          </Link>

          {/* Account button — always visible (next to heart) */}
          <div className="relative">
            {user && typeof user === "object" ? (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); setAccountOpen((o) => !o); }}
                  data-testid="account-btn"
                  className="hover:text-[#D4AF37] transition-colors inline-flex items-center"
                  aria-label="Account"
                >
                  <User strokeWidth={1.25} size={20} />
                </button>
                {accountOpen && (
                  <div
                    className="absolute right-0 top-full mt-3 min-w-[220px] bg-[#0A0A0A] border border-[#262626] shadow-xl"
                    data-testid="account-menu"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="px-4 py-3 border-b border-[#1a1a1a]">
                      <div className="font-serif-display" style={{ fontSize: 15, color: "#FFFFFF" }}>
                        {user.full_name || user.email}
                      </div>
                      <div className="text-[11px]" style={{ color: "rgba(255,255,255,0.5)" }}>
                        {user.email}
                      </div>
                    </div>
                    <Link
                      to="/my-orders"
                      onClick={() => setAccountOpen(false)}
                      data-testid="menu-my-orders"
                      className="flex items-center gap-2 px-4 py-3 hover:bg-[#141414] hover:text-[#F5D565] transition-colors"
                    >
                      <Package size={14} strokeWidth={1.5} />
                      <span className="font-ui" style={{ fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase" }}>My Orders</span>
                    </Link>
                    <button
                      onClick={() => { setAccountOpen(false); logout(); navigate("/"); }}
                      data-testid="menu-logout"
                      className="w-full flex items-center gap-2 px-4 py-3 hover:bg-[#141414] hover:text-[#F5D565] transition-colors"
                    >
                      <LogOut size={14} strokeWidth={1.5} />
                      <span className="font-ui" style={{ fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase" }}>Sign out</span>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <Link
                to="/login"
                data-testid="signin-btn"
                aria-label="Sign in"
                className="hover:text-[#D4AF37] transition-colors inline-flex items-center"
              >
                <User strokeWidth={1.25} size={20} />
              </Link>
            )}
          </div>

          <button
            data-testid="cart-btn"
            onClick={() => setCartOpen(true)}
            className="hover:text-[#D4AF37] transition-colors relative"
            aria-label="Cart"
          >
            <ShoppingBag strokeWidth={1.25} size={20} />
            {itemCount > 0 && (
              <span className="absolute -top-1 -right-2 text-[10px] text-[#D4AF37]">
                {itemCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 bg-[#0A0A0A] grain" data-testid="mobile-menu">
          <div className="flex justify-end p-6">
            <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close">
              <X strokeWidth={1.25} size={24} className="text-[#F5F5F5]" />
            </button>
          </div>
          <div className="flex flex-col items-center gap-6 mt-12 font-serif-display text-2xl">
            <Link to="/shop" onClick={() => setMobileOpen(false)} className="link-gold">Shop All</Link>
            <button
              onClick={() => { setMobileOpen(false); setSearchOpen(true); }}
              className="link-gold inline-flex items-center gap-2"
              data-testid="mobile-search-btn"
            >
              <Search size={18} /> Search
            </button>
            <button
              onClick={() => {
                setMobileOpen(false);
                setFiltersOpen(true);
              }}
              className="link-gold inline-flex items-center gap-2"
              data-testid="mobile-filters-btn"
            >
              <SlidersHorizontal size={18} /> Filters
            </button>
            <Link to="/wishlist" onClick={() => setMobileOpen(false)} className="link-gold">Wishlist</Link>
            {user && typeof user === "object" ? (
              <>
                <Link to="/my-orders" onClick={() => setMobileOpen(false)} className="link-gold" data-testid="mobile-my-orders">My Orders</Link>
                <button onClick={() => { setMobileOpen(false); logout(); navigate("/"); }} className="link-gold" data-testid="mobile-signout">Sign out</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setMobileOpen(false)} className="link-gold" data-testid="mobile-signin">Sign in</Link>
            )}
          </div>
        </div>
      )}
      <SearchDropdown open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}
